import { useEffect, useRef, useState } from "react";
import { Container } from "@/components/atoms/index";
import {
  Box,
  CircularProgress,
  Grid,
  Typography,
  Fade,
  Grow,
  Fab,
} from "@mui/material";
import { FilterPanel } from "@/components/organisms";
import { useCompanyContext, useFilterCompanies } from "@/providers/contexts";
import { CompanyCard } from "@/components/organisms/CompanyCard";
import {
  ErrorState,
  LoadingState,
  NoMatchState,
  UnauthenticatedState,
} from "@/components/views";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Company } from "@/types/company";
import { useUserContext } from "@/providers/contexts";
import { debounce } from "lodash";

export const Directory = () => {
  /* --------------------- Hooks --------------------- */
  const { setCompanyId, setCompanyModalOpen } = useCompanyContext();

  const {
    pagination,
    setPage,
    page,
    companies,
    error,
    loading,
    resetFilters,
    getNoResultsMessage,
    noMatchingResults,
  } = useFilterCompanies();

  const { isAuthenticated } = useUserContext();

  const [showScrollTop, setShowScrollTop] = useState<boolean>(false);
  const loader = useRef<HTMLDivElement | null>(null);

  /* --------------------- Lifecycle Methods --------------------- */

  // Infinite scroll trigger
  useEffect(() => {
    const debouncedLoadNextPage = debounce(() => {
      setPage(prev => prev + 1);
    }, 300);

    const observerCallback: IntersectionObserverCallback = entries => {
      const entry = entries[0];
      if (entry.isIntersecting && pagination?.next) {
        debouncedLoadNextPage();
      }
    };

    const observer = new IntersectionObserver(observerCallback, {
      threshold: 0.5,
    });

    const currentLoader = loader.current;

    if (currentLoader) {
      observer.observe(currentLoader);
    }

    return () => {
      if (currentLoader) {
        observer.unobserve(currentLoader);
      }
      debouncedLoadNextPage.cancel();
    };
  }, [pagination?.next]);

  // Scroll-to-top logic
  useEffect(() => {
    const onScroll = () => {
      setShowScrollTop(window.pageYOffset > 300);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!isAuthenticated && !loading) {
    return <UnauthenticatedState />;
  }

  if (loading && page === 1) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState errors={error} />;
  }

  if (noMatchingResults) {
    return (
      <NoMatchState message={getNoResultsMessage()} onReset={resetFilters} />
    );
  }
  /* --------------------- Handles --------------------- */
  const handleCompanyCardClick = (companyId: Company["id"]) => {
    setCompanyId(companyId);
    setCompanyModalOpen(true);
  };

  return (
    <Container componentName="directory">
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <FilterPanel
            page="directory"
            resetFilters={resetFilters}
            renderJobSetting={false}
            renderDepartments={false}
            renderJobRoles={false}
            renderSalaryRange={false}
            renderDate={false}
            renderCompanyType={true}
            renderFundingType={true}
            renderOperatingStatus={true}
            renderYearFounded={true}
          />
        </Grid>

        {companies?.length === 0 ? (
          <Typography>No companies found.</Typography>
        ) : (
          companies?.map((company: Company) => (
            <Grid item key={company.id} xs={12} sm={4} md={4}>
              <Grow in={true} timeout={700}>
                <div>
                  <CompanyCard
                    onClick={() => handleCompanyCardClick(company.id)}
                    company={company}
                    loading={loading}
                    error={error}
                  />
                </div>
              </Grow>
            </Grid>
          ))
        )}

        {pagination?.next && (
          <Grid item xs={12}>
            <Box display="flex" justifyContent="center" mt={2}>
              <CircularProgress />
            </Box>
            <div ref={loader} />
          </Grid>
        )}
      </Grid>

      {showScrollTop && (
        <Fade in={true} timeout={700}>
          <Fab
            color="primary"
            onClick={scrollToTop}
            sx={{
              position: "fixed",
              bottom: 16,
              right: 16,
              zIndex: 1000,
            }}
            aria-label="scroll back to top"
          >
            <KeyboardArrowUpIcon />
          </Fab>
        </Fade>
      )}
    </Container>
  );
};
